import React, { useCallback, useEffect, useRef, useState } from 'react';
import { registerConfirmHost } from '../../utils/confirm';
import ConfirmDialog from './ConfirmDialog';

// Mount once (App.js). Renders one dialog at a time and queues the rest.
const ConfirmHost = () => {
  const [current, setCurrent] = useState(null);
  const queue = useRef([]);

  const next = useCallback(() => {
    setCurrent(queue.current.shift() || null);
  }, []);

  useEffect(
    () =>
      registerConfirmHost(request => {
        setCurrent(active => {
          if (active) {
            queue.current.push(request);
            return active;
          }
          return request;
        });
      }),
    []
  );

  const finish = (request, value) => {
    request.resolve(value);
    next();
  };

  const handleConfirm = async reason => {
    const request = current;
    try {
      await request.onConfirm?.(reason);
    } catch {
      finish(request, false);
      return;
    }
    finish(request, reason || true);
  };

  // Keep showing the last request while the dialog fades out.
  const shown = useRef(null);
  if (current) shown.current = current;
  const {
    resolve: _resolve,
    onConfirm: _onConfirm,
    ...dialogProps
  } = shown.current || {};

  return (
    <ConfirmDialog
      {...dialogProps}
      title={current?.title || ''}
      isOpen={Boolean(current)}
      onConfirm={handleConfirm}
      onCancel={() => finish(current, false)}
    />
  );
};

export default ConfirmHost;
